"use client";

import { useEffect, useState, type FormEvent } from "react";

type ToastState = {
  message: string;
  tone: "success" | "error";
} | null;

export const ContactForm = () => {
  const [projectType, setProjectType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<ToastState>(null);

  useEffect(() => {
    if (!toast) return;

    const timeoutId = window.setTimeout(() => {
      setToast(null);
    }, 4200);

    return () => window.clearTimeout(timeoutId);
  }, [toast]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    setIsSubmitting(true);
    setToast(null);

    try {
      const formData = new FormData(form);
      const payload = {
        name: formData.get("name")?.toString() || "",
        email: formData.get("email")?.toString() || "",
        company: formData.get("company")?.toString() || "",
        projectType: formData.get("project-type")?.toString() || "",
        projectTypeOther: formData.get("project-type-other")?.toString() || "",
        budget: formData.get("budget")?.toString() || "",
        overview: formData.get("overview")?.toString() || "",
      };

      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || "Failed to submit inquiry");
      }

      form.reset();
      setProjectType("");
      setToast({
        tone: "success",
        message: "Inquiry submitted. We’ll get back to you soon.",
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to submit inquiry";
      setToast({
        tone: "error",
        message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="flex flex-col gap-2">
          <label className="font-label-sm text-label-sm text-secondary uppercase tracking-widest" htmlFor="name">Name</label>
          <input className="w-full bg-transparent border border-surface-variant rounded-DEFAULT px-4 py-3 font-body-md text-body-md text-primary focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none" id="name" name="name" placeholder="Jane Doe" required type="text" />
        </div>
        <div className="flex flex-col gap-2">
          <label className="font-label-sm text-label-sm text-secondary uppercase tracking-widest" htmlFor="email">Email</label>
          <input className="w-full bg-transparent border border-surface-variant rounded-DEFAULT px-4 py-3 font-body-md text-body-md text-primary focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none" id="email" name="email" placeholder="jane@company.com" required type="email" />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <label className="font-label-sm text-label-sm text-secondary uppercase tracking-widest" htmlFor="company">Company / Organization</label>
        <input className="w-full bg-transparent border border-surface-variant rounded-DEFAULT px-4 py-3 font-body-md text-body-md text-primary focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none" id="company" name="company" placeholder="Company Name" type="text" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="flex flex-col gap-2">
          <label className="font-label-sm text-label-sm text-secondary uppercase tracking-widest" htmlFor="project-type">Project Scope</label>
          <div className="relative">
            <select defaultValue="" onChange={(e) => setProjectType(e.target.value)} className="w-full bg-transparent border border-surface-variant rounded-DEFAULT px-4 py-3 font-body-md text-body-md text-primary focus:border-primary focus:ring-1 focus:ring-primary appearance-none cursor-pointer transition-all outline-none" id="project-type" name="project-type" required>
              <option disabled value="">Select an option</option>
              <option value="web">Web</option>
              <option value="ios">iOS App</option>
              <option value="python">Python</option>
              <option value="other">Other</option>
            </select>
            <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-secondary">arrow_drop_down</span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label className="font-label-sm text-label-sm text-secondary uppercase tracking-widest" htmlFor="budget">Estimated Budget</label>
          <input className="w-full bg-transparent border border-surface-variant rounded-DEFAULT px-4 py-3 font-body-md text-body-md text-primary focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none" id="budget" name="budget" placeholder="e.g., $500, $1k+" type="text" />
        </div>
      </div>
      
      {projectType === "other" && (
        <div className="flex flex-col gap-2 overflow-hidden">
          <label className="font-label-sm text-label-sm text-secondary uppercase tracking-widest" htmlFor="project-type-other">Please specify</label>
          <input className="w-full bg-transparent border border-surface-variant rounded-DEFAULT px-4 py-3 font-body-md text-body-md text-primary focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none" id="project-type-other" name="project-type-other" placeholder="Describe your project type..." required type="text" />
        </div>
      )}

      <div className="flex flex-col gap-2">
        <label className="font-label-sm text-label-sm text-secondary uppercase tracking-widest" htmlFor="overview">Project Overview</label>
        <textarea className="w-full bg-transparent border border-surface-variant rounded-DEFAULT px-4 py-3 font-body-md text-body-md text-primary focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none resize-y" id="overview" name="overview" placeholder="Describe the core objectives, technical constraints, and desired outcomes..." required rows={5}></textarea>
      </div>
      <div className="pt-4 border-t border-surface-variant">
        <button 
          className="bg-primary text-on-primary font-label-sm text-label-sm uppercase tracking-widest px-8 py-4 rounded hover:bg-surface-tint transition-colors w-full md:w-auto flex items-center justify-center gap-2 group shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed" 
          disabled={isSubmitting}
          type="submit"
        >
          {isSubmitting ? "Submitting..." : "Submit Inquiry"}
          <span className="material-symbols-outlined text-[16px] group-hover:translate-x-1 transition-transform">arrow_forward</span>
        </button>
      </div>

      {toast ? (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm">
          <div
            className={`rounded-DEFAULT border px-4 py-3 shadow-lg backdrop-blur-md ${
              toast.tone === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-950"
                : "border-rose-200 bg-rose-50 text-rose-950"
            }`}
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined mt-0.5 text-[18px]">
                {toast.tone === "success" ? "check_circle" : "error"}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-label-sm text-label-sm uppercase tracking-widest">
                  {toast.tone === "success" ? "Submitted" : "Submission failed"}
                </p>
                <p className="mt-1 text-sm leading-6">{toast.message}</p>
              </div>
              <button
                aria-label="Dismiss notification"
                className="text-current/60 hover:text-current transition-colors"
                type="button"
                onClick={() => setToast(null)}
              >
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </form>
  );
};
